import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import {
    LDFeatureFlags,
    LIVE_ALERTS_TOGGLE_CLICK_IN_FEATURE_BAR_COLLAPSED,
    LIVE_ALERTS_TOGGLE_CLICK_IN_FEATURE_BAR_EXPANDED,
} from '@frontend/sports/launch-darkly/feature';
import { DialogSettings, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UserService } from '@frontend/sports/user/feature';
import { ISucceededSlipPickResult } from 'packages/sports/web/libs/platform-lib/src/betslip/modules/betplacement/models';
import { Subscription } from 'rxjs';

import { AlertConfigService } from '../alert-config.service';
import { AlertSelectorService } from '../alert-selector/alert-selector.service';
import { AlertSubscriptionService } from '../alert-subscription.service';
import { AlertToastMessagesService } from '../alert-toast-messages.service';
import { AlertTrackingService } from '../alert-tracking.service';
import { getBetslipModel, getEventModel, getMyBetsModel, getScoreboardModel } from '../alert-utils';
import { LaunchDarklyAlertsService } from '../launch-darkly-alerts-service';
import { AlertEvent, AlertMetaData, IMyBetsAlertData, IScoreboardLiveAlert, NotificationEnabled, Subscriptions } from '../model';
import { NativeAlertsService } from '../native-alerts.service';

type AlertModel =
    | { type: 'event'; data: EventModel }
    | { type: 'myBets'; data: IMyBetsAlertData[] }
    | { type: 'betSlip'; data: ISucceededSlipPickResult[] }
    | { type: 'scoreboard'; data: IScoreboardLiveAlert };

@Component({
    selector: 'ms-alert',
    template: '',
    host: {
        '[class.alert-enabled]': 'showAlerts()',
        '[class.alert-selected]': 'selected',
        '[class.alert-deselected]': '!selected',
        '[class.sports-alert]': 'showAlerts() && !multiple() && !!selected',
        '[class.sports-alert-o]': 'showAlerts() && !multiple() && !selected',
        '[class.sports-alert-multiple]': 'showAlerts() && multiple() && !!selected',
        '[class.sports-alert-multiple-o]': 'showAlerts() && multiple() && !selected',
    },
})
export class AlertButtonComponent implements OnDestroy {
    @Input() disable = false;
    @Input() alertMetaData: AlertMetaData;
    @Input() set eventModel(value: EventModel | null) {
        if (value) {
            this._model.set({ type: 'event', data: value });
        }
    }
    @Input() set myBetsModel(value: IMyBetsAlertData[] | null) {
        if (value) {
            this._model.set({ type: 'myBets', data: value });
        }
    }
    @Input() set betSlipModel(value: ISucceededSlipPickResult[] | null) {
        if (value) {
            this._model.set({ type: 'betSlip', data: value });
        }
    }
    @Input() set scoreboardModel(value: IScoreboardLiveAlert | null) {
        if (value) {
            this._model.set({ type: 'scoreboard', data: value });
        }
    }
    @Input() tracking?: string;
    @Input() selected?: boolean;
    @Output() alertEnabled = new EventEmitter<void>();
    @Input() isExpanded = false;
    @Input() trackLiveAlertsMetrics: boolean;

    private readonly alertConfig = inject(AlertConfigService);
    private readonly toastMessageService = inject(AlertToastMessagesService);
    private readonly selectorService = inject(AlertSelectorService);
    private readonly subscriptionService = inject(AlertSubscriptionService);
    private readonly userService = inject(UserService);
    private readonly modalDialogService = inject(ModalDialogService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly redirectService = inject(RedirectHelperService);
    private readonly nativeAlertsService = inject(NativeAlertsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly alertTrackingService = inject(AlertTrackingService);

    private readonly _model = signal<AlertModel | null>(null);

    readonly events = computed(() => {
        const model = this._model();

        if (!model) {
            return this.selectorService.model;
        }

        switch (model.type) {
            case 'event':
                return getEventModel(model.data);
            case 'myBets':
                return getMyBetsModel(model.data, this.alertMetaData);
            case 'betSlip':
                return getBetslipModel(model.data, this.alertMetaData, (sportId) => this.alertConfig.alertsPerSport().has(sportId));
            case 'scoreboard':
                return getScoreboardModel(model.data);
            default:
                return this.selectorService.model;
        }
    });

    readonly multiple = computed(() => {
        return this.events() && this.events().length > 1;
    });

    readonly showAlerts = computed(() => {
        return this.alertConfig.alertsEnabled() && this.events() && this.alertConfig.alertsPerSport().has(this.events()[0]?.sportId);
    });

    private readonly alertEffect = effect(() => {
        if (this.showAlerts()) {
            this.alertEnabled.emit();
            this.subscribe();
        }
    });

    private dialog?: ModalRef;
    private subscription: Subscription;
    private readonly launchDarklyAlertsService = inject(LaunchDarklyAlertsService);

    ngOnDestroy(): void {
        this.alertEffect.destroy();
    }

    @HostListener('click', ['$event'])
    clicked(event: Event): void {
        if (!this.userService.isAuthenticated) {
            this.redirectService.goToLogin({ appendReferrer: true });

            return;
        }

        if (this.disable) {
            return;
        }

        if (this.selected && !this.multiple()) {
            this.unsubscribe();
        } else {
            this.openDialog();
        }

        event.stopPropagation();
    }

    private unsubscribe(): void {
        const subscriptions = this.subscriptionService.getSubscriptions(this.events());
        if (subscriptions) {
            this.subscriptionService.unsubscribeAll(subscriptions).then(() => {
                this.selected = false;
                this.alertTrackingService.trackUserInteractionAlerts(this.events(), false, this.tracking);
                this.alertTrackingService.trackAlertsSuccess(this.events(), false, this.tracking);
                this.toastMessageService.showUnsubscribedToast();
            });
        }
    }

    private async openDialog(): Promise<void> {
        if (!this.alertConfig.isAppNotificationsEnabled()) {
            await this.launchAlertSelector();
        } else {
            this.verifyAndLaunchAlertsSelector();
        }
    }

    private async launchAlertSelector(): Promise<void> {
        if (this.dialog) {
            return;
        }

        this.alertTrackingService.trackUserInteractionAlerts(this.events(), true, this.tracking);
        this.selectorService.model = this.events().filter((event: AlertEvent) => this.alertConfig.alertsPerSport().has(event.sportId));
        this.selectorService.showSelector(this.multiple(), this.tracking || '', !!this.selected);

        const settings: DialogSettings = {
            showPageHeader: false,
            cssClass: 'alerts-selector-popup',
            isModalDialog: false,
            fit: false,
        };

        this.dialog = this.modalDialogService.openDialog(
            await import('../alert-selector/alert-selector.component').then((m) => m.AlertSelectorComponent),
            {
                data: { trackLiveAlertsMetrics: this.trackLiveAlertsMetrics },
                settings,
            },
        );
        this.dialog?.result.finally(() => {
            this.dialog = undefined;
        });

        if (this.trackLiveAlertsMetrics) {
            this.trackMetricsForLiveAlertsInFeatureBar();
        }
    }

    private trackMetricsForLiveAlertsInFeatureBar(): void {
        const metricKey = this.isExpanded ? LIVE_ALERTS_TOGGLE_CLICK_IN_FEATURE_BAR_EXPANDED : LIVE_ALERTS_TOGGLE_CLICK_IN_FEATURE_BAR_COLLAPSED;
        this.launchDarklyAlertsService.track(LDFeatureFlags.EnableLiveAlerts, metricKey, 1);
    }

    private verifyAndLaunchAlertsSelector(): void {
        if (this.dialog) {
            return;
        }

        this.subscription?.unsubscribe();
        this.subscription = this.nativeAlertsService.subscribeToNotificationCheck().subscribe((data) => {
            if (data?.parameters?.notificationEnabled === NotificationEnabled.Yes) {
                this.launchAlertSelector();
            }
            this.subscription.unsubscribe();
        });
    }

    private subscribe(): void {
        this.subscriptionService.event.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => this.updateProperty(res));
    }

    private updateProperty(subscription: Subscriptions): void {
        if (!subscription || this.disable) {
            return;
        }

        const wasSelected = this.selected;

        this.selected = false;
        this.events().forEach((item: AlertEvent) => {
            item.selected = !!this.subscriptionService.getSubscription(item.eventId, item.eventGroupId);

            if (item.selected) {
                this.selected = true;
            }
        });

        if (wasSelected !== this.selected) {
            this.changeDetector.markForCheck();
        }
    }
}
